import { Link } from '@tanstack/react-router';
import { T, useGT, Var } from 'gt-react';
import { ArrowSquareOutIcon, FunnelSimpleIcon } from '@phosphor-icons/react';
import type { VendorPortalFile } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import paths from '@/paths';

/**
 * Where a listed file came from: the run that downloaded it (or the run an upload is
 * attached to), with a filter to that run's files, and for a file a user uploaded by
 * hand an Uploaded badge naming who, so nobody mistakes it for a portal download.
 */
export function VendorPortalFileSource({
  file,
  onOnlyRun,
}: {
  file: VendorPortalFile;
  onOnlyRun: (workflowRunId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      {file.source === 'upload' ? <UploadedBadge file={file} /> : null}
      {file.workflowRunId ? (
        <RunLinks
          workflowId={file.workflowId}
          workflowRunId={file.workflowRunId}
          onOnlyRun={onOnlyRun}
        />
      ) : null}
    </div>
  );
}

function UploadedBadge({ file }: { file: VendorPortalFile }) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Badge variant="secondary">
        <T context="Vendor portals badge on a file a user uploaded by hand rather than a workflow downloading it">
          Uploaded
        </T>
      </Badge>
      {file.uploadedBy ? (
        <span className="text-xs text-muted-foreground">
          <T context="Vendor portals: who uploaded a file by hand; the variable is the user's name">
            by <Var>{file.uploadedBy}</Var>
          </T>
        </span>
      ) : null}
    </span>
  );
}

function RunLinks({
  workflowId,
  workflowRunId,
  onOnlyRun,
}: {
  workflowId: string;
  workflowRunId: string;
  onOnlyRun: (workflowRunId: string) => void;
}) {
  const gt = useGT();
  const onlyThisRun = gt('Show only files from this run', {
    context:
      'Vendor portals button that filters the list to the workflow run that downloaded this file',
  });
  return (
    <span className="inline-flex items-center">
      <Link
        to={paths.authenticated.workflows.run}
        params={{ workflowId, workflowRunId }}
        className="inline-flex items-center gap-1 hover:underline"
      >
        <T context="Vendor portals table link to the workflow run that downloaded the file">
          View run
        </T>
        <ArrowSquareOutIcon className="h-3 w-3" />
      </Link>
      <Button
        size="icon"
        variant="ghost"
        className="ml-1 h-6 w-6"
        onClick={() => onOnlyRun(workflowRunId)}
        aria-label={onlyThisRun}
        title={onlyThisRun}
      >
        <FunnelSimpleIcon className="h-3 w-3" />
      </Button>
    </span>
  );
}
